import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Wrench,
  User,
  FileText,
  Sparkles,
  Camera,
  Settings,
  X,
  Loader2,
  ClipboardList,
  AlertTriangle,
  Calendar,
  Flag,
  History,
  Check,
  UploadCloud,
  Plus,
  Trash2,
} from "lucide-react";
import { maintenanceAPI, errorAPI } from "../api";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import clsx from "clsx";

const MaintenanceExecution = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Component State
  const [steps, setSteps] = useState([]);
  const [finalNotes, setFinalNotes] = useState("");
  const [partsUsed, setPartsUsed] = useState([]);
  const [newPart, setNewPart] = useState("");

  // Data Queries
  const {
    data: log,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["maintenance-log-execution", id],
    queryFn: () => maintenanceAPI.getById(id).then((res) => res.data.data),
  });

  const { data: linkedBug } = useQuery({
    queryKey: ["linked-bug", log?.bug_id],
    queryFn: () => errorAPI.getById(log.bug_id).then((res) => res.data.data),
    enabled: !!log?.bug_id,
  });

  // Initialize state when data loads
  useEffect(() => {
    if (log) {
      // Use SOP steps if available, otherwise create a default manual step
      const baseSteps =
        log.sop_steps?.length > 0
          ? log.sop_steps
          : [{ instruction: "Document work performed and observations." }];

      const initialSteps = baseSteps.map((sopStep, index) => {
        const completedStep = log.steps_completed?.find(
          (cs) => cs.stepIndex === index,
        );
        return {
          stepIndex: index,
          instruction: sopStep.instruction,
          notes: completedStep?.notes || "",
          photoFile: null,
          photoPreview: completedStep?.photoUrl || null,
          isCompleted: !!completedStep,
        };
      });

      setSteps(initialSteps);
      setFinalNotes(log.notes || "");
      setPartsUsed(log.parts_used || []);
    }
  }, [log]);

  // Mutations
  const completeMutation = useMutation({
    mutationFn: (formData) => maintenanceAPI.complete(id, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["maintenance-log", id] });
      queryClient.invalidateQueries({ queryKey: ["maintenance"] });
      toast.success("Maintenance completed and logged successfully!");
      navigate(`/maintenance/${id}`);
    },
    onError: (err) =>
      toast.error("Failed to complete maintenance: " + err.message),
  });

  // Handlers
  const handleStepUpdate = (index, field, value) => {
    const newSteps = [...steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    setSteps(newSteps);
  };

  const handlePhotoChange = (index, file) => {
    if (file) {
      handleStepUpdate(index, "photoFile", file);
      handleStepUpdate(index, "photoPreview", URL.createObjectURL(file));
    }
  };

  const handleAddPart = () => {
    if (newPart.trim() !== "" && !partsUsed.includes(newPart.trim())) {
      setPartsUsed([...partsUsed, newPart.trim()]);
      setNewPart("");
    }
  };

  const handleRemovePart = (partToRemove) => {
    setPartsUsed(partsUsed.filter((part) => part !== partToRemove));
  };

  const handleSubmit = () => {
    // Basic validation
    const allStepsCompleted = steps.every((step) => step.isCompleted);
    if (!allStepsCompleted) {
      toast.error("Please complete all steps before submitting.");
      return;
    }
    if (!finalNotes.trim()) {
      toast.error("Please provide final observations before submitting.");
      return;
    }

    const formData = new FormData();
    formData.append("notes", finalNotes);
    formData.append("parts_used", JSON.stringify(partsUsed));

    const stepsCompletedPayload = steps.map((step) => ({
      stepIndex: step.stepIndex,
      notes: step.notes,
    }));
    formData.append("steps_completed", JSON.stringify(stepsCompletedPayload));

    steps.forEach((step, index) => {
      if (step.photoFile) {
        formData.append(`step_${index}_photo`, step.photoFile);
      }
    });

    completeMutation.mutate(formData);
  };

  if (isLoading)
    return (
      <div className="flex justify-center h-96 items-center">
        <div className="w-10 h-10 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  if (error || !log)
    return (
      <div className="text-center py-12 text-slate-400 font-medium">
        Maintenance record not found or could not be loaded.
      </div>
    );

  const aiPlan = log.ai_plan;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 space-y-6 pb-12">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 border-b border-slate-200 pb-6 pt-8">
        <div className="flex items-start gap-4">
          <Link
            to={`/maintenance/${id}`}
            className="p-3 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-slate-900">{log.title}</h1>
            </div>
            <div className="text-sm text-slate-500 mt-3 flex items-center gap-x-4 gap-y-1 flex-wrap">
              <div className="flex items-center gap-1.5">
                <Settings className="w-4 h-4" /> {log.machine_name}
              </div>
              <div className="flex items-center gap-1.5">
                <FileText className="w-4 h-4" />{" "}
                {log.sop_title || "General Maintenance"}
              </div>
              <div className="flex items-center gap-1.5">
                <User className="w-4 h-4" /> {log.technician_name}
              </div>
            </div>
          </div>
        </div>

        <div className="w-full sm:w-auto flex-shrink-0 flex gap-2 pt-2 sm:pt-0 pl-0 sm:pl-4">
          <button
            onClick={handleSubmit}
            disabled={completeMutation.isPending}
            className="btn btn-primary gap-2 shadow-lg w-full sm:w-auto justify-center"
          >
            {completeMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Check className="w-4 h-4" />
            )}
            Complete Task
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-6">
          <div className="bg-white border border-slate-200 rounded-3xl shadow-sm p-6">
            <h3 className="font-bold text-slate-800 flex items-center gap-2 mb-4">
              <ClipboardList className="w-5 h-5 text-blue-500" /> Execution
              Steps
            </h3>
            <div className="space-y-6">
              {steps.map((step, i) => (
                <div
                  key={i}
                  className="p-4 border border-slate-200 rounded-2xl bg-slate-50/50"
                >
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-xs font-bold border border-slate-200 flex-shrink-0 mt-1">
                      {i + 1}
                    </div>
                    <div className="flex-1 space-y-3">
                      <p className="text-slate-900 font-semibold text-sm">
                        {step.instruction}
                      </p>
                      <textarea
                        className="input w-full min-h-[80px]"
                        placeholder="Add notes..."
                        value={step.notes}
                        onChange={(e) =>
                          handleStepUpdate(i, "notes", e.target.value)
                        }
                        disabled={step.isCompleted}
                      />
                      <div className="flex flex-col sm:flex-row gap-4 items-center">
                        <label className="flex-1 w-full btn btn-secondary btn-sm gap-2 cursor-pointer">
                          <UploadCloud className="w-4 h-4" />
                          <span>
                            {step.photoFile
                              ? "Change Photo"
                              : "Upload Evidence"}
                          </span>
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*"
                            onChange={(e) =>
                              handlePhotoChange(i, e.target.files[0])
                            }
                            disabled={step.isCompleted}
                          />
                        </label>
                        {step.photoPreview && (
                          <div className="w-12 h-12 rounded-lg overflow-hidden border-2 border-white shadow-md flex-shrink-0">
                            <img
                              src={step.photoPreview}
                              alt="Preview"
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <button
                          onClick={() =>
                            handleStepUpdate(
                              i,
                              "isCompleted",
                              !step.isCompleted,
                            )
                          }
                          className={clsx(
                            "btn btn-sm w-full sm:w-auto",
                            step.isCompleted
                              ? "btn-success"
                              : "btn-outline-secondary",
                          )}
                        >
                          <Check className="w-4 h-4" />
                          {step.isCompleted ? "Completed" : "Mark Complete"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-3xl shadow-sm p-6">
            <h3 className="font-bold text-slate-800 flex items-center gap-2 mb-4">
              <FileText className="w-4 h-4 text-blue-500" /> Final Observations
              & Summary
            </h3>
            <textarea
              className="input w-full min-h-[120px]"
              placeholder="Provide a technical summary of the work performed, any unexpected findings, or recommendations for future maintenance..."
              value={finalNotes}
              onChange={(e) => setFinalNotes(e.target.value)}
            />
          </div>
        </div>

        <div className="lg:col-span-4 space-y-6">
          {log?.bug_id && aiPlan?.suggestion && (
            <div className="bg-slate-900 rounded-3xl p-6 text-white shadow-xl relative overflow-hidden border border-white/5">
              <Sparkles className="absolute -top-4 -right-4 w-24 h-24 text-white/10 rotate-12" />
              <div className="relative z-10 space-y-4">
                <h3 className="font-black text-[10px] uppercase tracking-widest text-blue-400 flex items-center gap-2">
                  <Sparkles className="w-3 h-3" /> AI Suggested Plan
                </h3>
                <div className="text-sm leading-relaxed text-slate-200 font-medium whitespace-pre-wrap border-l-2 border-blue-500/30 pl-4 py-1">
                  {aiPlan.suggestion}
                </div>
                {/* MODIFICATION: Added Evidence Sources */}
                <div className="space-y-2 pt-4 border-t border-white/10">
                  <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest">
                    Evidence Sources
                  </p>
                  <div className="grid grid-cols-1 gap-1.5">
                    {aiPlan.references?.map((ref, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 bg-white/5 p-2 rounded-xl"
                      >
                        {ref.type === "manual" ? (
                          <FileText className="w-3 h-3 text-blue-400" />
                        ) : (
                          <History className="w-3 h-3 text-amber-400" />
                        )}
                        <span className="text-[10px] font-bold text-slate-300 truncate">
                          {ref.title}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
          {linkedBug && (
            <div className="bg-white border border-slate-200 rounded-3xl shadow-sm p-6">
              <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2 text-rose-800">
                <AlertTriangle className="w-4 h-4" /> Original Error Log
              </h3>
              <p className="text-slate-800 text-sm font-medium leading-relaxed italic p-4 bg-rose-50 border border-rose-100 rounded-xl">
                "{linkedBug.description}"
              </p>
            </div>
          )}
          <div className="bg-white border border-slate-200 rounded-3xl shadow-sm p-6">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Wrench className="w-4 h-4 text-slate-400" /> Parts & Consumables
            </h3>
            <div className="space-y-2">
              {partsUsed.map((part, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between bg-slate-50 p-2 rounded-lg"
                >
                  <span className="text-sm font-medium text-slate-700">
                    {part}
                  </span>
                  <button
                    onClick={() => handleRemovePart(part)}
                    className="p-1 hover:bg-slate-200 rounded-md"
                  >
                    <Trash2 className="w-3.5 h-3.5 text-slate-400" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-4">
              <input
                type="text"
                className="input input-sm flex-1"
                placeholder="Enter Part ID or Name"
                value={newPart}
                onChange={(e) => setNewPart(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleAddPart()}
              />
              <button
                onClick={handleAddPart}
                className="btn btn-primary btn-sm"
              >
                <Plus className="w-4 h-4" /> Add
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceExecution;
